import { MarketSegment } from '@/creditflowresearch/domain/Market/types/MarketSegment';
import { IDictionaryMarketSegment } from '@/creditflowresearch/domain/vo/IDictionary';
import BaseDomainEntity from '@/creditflowresearch/domain/BaseDomainEntity';
import { SegmentIdByType, SegmentTypeById } from '@/creditflowresearch/domain/Segment/constants';

abstract class Segment extends BaseDomainEntity<Segment, IDictionaryMarketSegment> {
  static idRegistry = new Map<string, Segment>();

  protected readonly name: MarketSegment;

  protected readonly abbreviation: MarketSegment;

  constructor(segmentData: IDictionaryMarketSegment) {
    super(segmentData);
    this.validateSegmentData(segmentData);
    this.name = segmentData.name as MarketSegment;
    this.abbreviation = segmentData.name as MarketSegment;
  }

  getAbbreviation(): MarketSegment {
    return this.abbreviation;
  }

  private validateSegmentData(segmentData: IDictionaryMarketSegment): void {
    if (!segmentData?.id) {
      throw new Error('Segment data must have an id');
  }
    if (!segmentData?.name) {
      throw new Error('Segment data must have a name');
    }
  }

  static getId(type: string): string | null {
    return SegmentIdByType[type as MarketSegment] || null;
  }

  static getAbbreviation(segmentId: string): MarketSegment | null {
    return SegmentTypeById[segmentId] || null;
  }

  static isOfType(identifier: string, type: MarketSegment): boolean {
    const typeById = SegmentTypeById[identifier];
    const typeByName = SegmentIdByType[identifier as MarketSegment] ? identifier as MarketSegment : undefined;

    return typeById === type || typeByName === type;
  }

  static areAllOfType(identifiers: string[], type: MarketSegment): boolean {
    if (!identifiers?.length || !type) {
      return false;
    }

    return identifiers.every((identifier) => Segment.isOfType(identifier, type));
  }

  static areAllOfTypes(identifiers: string[], types: MarketSegment[]): boolean {
    if (!identifiers?.length || !types?.length) {
      return false;
    }

    return identifiers.every((identifier) =>
      types.some((type) => Segment.isOfType(identifier, type)),
    );
  }

  static hasAnyOfType(identifiers: string[], type: MarketSegment): boolean {
    if (!identifiers?.length || !type) {
      return false;
    }

    return identifiers.some((identifier) => Segment.isOfType(identifier, type));
  }

  static containAllTypes(identifiers: string[], types: MarketSegment[]): boolean {
    if (!identifiers?.length || !types?.length) {
      return false;
    }

    return types.every((type) => Segment.hasAnyOfType(identifiers, type));
  }

  getName() {
    return this.name;
  }
}

export default Segment;
